import React, { forwardRef, useContext, useRef, useState } from 'react';
import classes from './SlateWrapper.module.scss';
import { SlateEditor } from '../SlateEditor';
import { SlateToolbar } from '../SlateToolbar';
import { CustomParagraph } from '../../../../models/slate-editor';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { ReactEditor } from 'slate-react';
import { createEditor, Editor, Transforms } from 'slate';
import { useDidMount } from '../../../../hooks/useDidMount';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { createEditorWithPlugins } from '../helpers/createEditorWithPlugins';
import { SlateToolbarViewProps } from '../SlateToolbar/SlateToolbar.view';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { IconsEnum } from '../../../../constants/icons';
import { ErrorMessageSeverity } from 'constants/form';
import { hasSeverity } from 'modules/PostEditor/validators/helpers/severity-checker';
import { getErrorMessages } from 'modules/PostEditor/validators/helpers/getErrorMessages';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import { PostEditorContextEnum } from 'models/post-editor';
import { PostEditorErrorKeys } from 'models/validator-props';
import { PostStatus } from '../../../../models/graphql';
import { getCustomizationProfileId } from '../../validators/helpers/customization-getter';
import { AppContext } from '../../../../providers/AppProvider';
import { VariationDndProps } from '../DraggableVariation/DraggableVariation.view';
import { SetIndexPostEditorTabAction } from '../../states/PostEditorGroupDataActions';
import { Debugger } from '../../../../components/containers/Debugger';
import { usePostLinkSettings } from '../../../../hooks/usePostLinkSettings';
import { POST_EDITOR_ERROR_CLASS } from '../../../../constants/styleConstants';

export type SlateWrapperViewProps = {
  //
  addVariation: VoidFunction;
  addComment: SlateToolbarViewProps['addComment'];
  text: CustomParagraph[];
  id: number;
  canRemove: boolean;
  hashtagsCount?: number;
  publishNext: boolean;
  isLast: boolean;
  borderErrorType: 'warning' | 'error' | 'none';
  dragHandler: JSX.Element | null;
  dragableProps: VariationDndProps;
  isDragging: boolean;
  isSelfDragging: boolean;
};

const warningsList: PostEditorErrorKeys[] = [];
const errorsList: PostEditorErrorKeys[] = [
  'pinterestDuplicatesCheck',
  'preventTwDuplicate',
  'scheduleTextOrMediaVariations'
];

const variationWarningList: PostEditorErrorKeys[] = ['facebookTwitterLineBreakLimit', 'pinterestLineBreakLimit'];
const variationErrorList: PostEditorErrorKeys[] = [
  ...variationWarningList,
  'facebookContentRequired',
  'facebookTextCharLimit',
  'googleTextCharLimit',
  'instagramHashtagLimit',
  'instagramTextCharLimit',
  'linkedinTextCharLimit',
  'pinterestTextCharLimit',
  'tikTokTextCharLimit',
  'twitterTextCharLimit'
];

export const SlateWrapperView = forwardRef<HTMLDivElement, SlateWrapperViewProps>((props, ref): JSX.Element => {
  const {
    text,
    id,
    canRemove,
    addVariation,
    addComment,
    hashtagsCount,
    publishNext,
    isLast,
    borderErrorType,
    dragHandler,
    dragableProps,
    isSelfDragging,
    isDragging
  } = props;
  const { postEditorContext } = useContext(AppContext);
  const { dispatch, editors, state } = useContext(PostEditorContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState, tabsDispatch] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const postLinkData = usePostLinkSettings();
  const [editor] = useState(() => createEditorWithPlugins(createEditor()));
  const [editorRefs, setEditor] = editors;
  const cssRef = useRef<HTMLDivElement>(null);
  const [mount, setMount] = useState<boolean>(false);
  const postGroup = state.postGroups[postGroupIndex];
  const { handlerId } = dragableProps;
  const tabsType = tabsState.tabs.tabsType;

  useDidMount(() => () => {
    // remove editor from editors
    setEditor({ type: 'remove', value: id })(editorRefs, postGroupIndex);
  });

  const openAiAssistant = () => {
    const payload: SetIndexPostEditorTabAction['payload'] =
      tabsType === 'single' ? { side: 'left', value: 3 } : { side: 'right', value: 2 };
    tabsDispatch({ type: 'setIndex', payload });
  };

  const initializeEditor = () => {
    // we use timeout 1 here to make sure all dom rendering has been done before we fire initialization
    setTimeout(() => {
      if (postEditorContext.type !== PostEditorContextEnum.import) {
        ReactEditor.focus(editor);
      }
      const { selection } = editor;
      if (!selection) {
        Transforms.select(editor, Editor.end(editor, []));
      }
      if (editor && !editorRefs.current[postGroupIndex].find(e => e.id === id)) {
        editor.setPostLinkData(postLinkData);
        setEditor({ type: 'add', value: { id, editor } })(editorRefs, postGroupIndex);
      }
    }, 1);
  };

  const { customizeContentPerProfile, posts, errors } = postGroup;
  const customizedProfileId = getCustomizationProfileId(postGroup, customizeIndex);
  const isCustomizingByProfile = customizeContentPerProfile && typeof customizeIndex === 'number';

  const hasErrorVariation = hasSeverity(
    errors,
    ErrorMessageSeverity.error,
    customizedProfileId,
    id,
    variationErrorList
  );
  const hasWarningVariation =
    !hasErrorVariation &&
    hasSeverity(errors, ErrorMessageSeverity.warning, customizedProfileId, id, variationWarningList);

  const isPostDraft = isCustomizingByProfile
    ? posts[customizeIndex].postStatus === PostStatus.DRAFT
    : postGroup.groupPostAttributes.postStatus === PostStatus.DRAFT;
  const hasError =
    !isPostDraft && hasSeverity(errors, ErrorMessageSeverity.error, customizedProfileId, undefined, errorsList);
  const warningErrors: PostEditorErrorKeys[] = isPostDraft ? errorsList : warningsList;
  const hasWarning =
    !hasError && hasSeverity(errors, ErrorMessageSeverity.warning, customizedProfileId, undefined, warningErrors);
  const errorMessages = getErrorMessages(errors, customizedProfileId, undefined, errorsList);

  const errorVariationMessages = getErrorMessages(errors, customizedProfileId, id, variationErrorList);
  const warningVariationMessages = getErrorMessages(errors, customizedProfileId, id, variationWarningList);

  useDidMount(() => {
    setMount(true);
    return () => {
      // set mount to false makes a fade out animation on unmount, and unregister on form instance is needed to make sure we reset the values for varyMedia to default, since if we decide to add media again later on, we would end up having the previous values set as default, since useForm by default keeps values. we need explicitly to say we don't want to keep values (both default (which are undefined always) and current)
      setMount(false);
    };
  });
  const boxClasses = classNames(classes.TextEditor, {
    [classes.TextEditorIsDragable]: !!dragHandler,
    [classes.TextEditorError]: hasErrorVariation && !isDragging,
    [classes.TextEditorWarning]: !hasErrorVariation && hasWarningVariation && !isDragging,
    [classes.TextEditorHasParentError]: borderErrorType === 'error',
    [classes.TextEditorHasParentWarning]: borderErrorType === 'warning',
    [classes.Dragging]: isSelfDragging,
    [POST_EDITOR_ERROR_CLASS]: borderErrorType === 'error' || hasErrorVariation
  });

  return (
    <CSSTransition
      in={mount}
      timeout={300}
      classNames={'fade'}
      nodeRef={cssRef}
      unmountOnExit
      onEntered={() => initializeEditor()}>
      <div ref={cssRef} className={boxClasses}>
        <div
          ref={ref}
          data-key={`${typeof customizeIndex === 'number' ? customizeIndex + '-' : ''}slate-${id}`}
          data-handler-id={handlerId}>
          <div className={'grid-x align-top'}>
            {dragHandler ? <div className={'cell shrink'}>{dragHandler}</div> : null}
            <div className={'cell auto'}>
              <Debugger tooltipContent={`Slate Text: ${id}`} modalContent={text} />
              <SlateEditor text={text} id={id} editor={editor} isDragging={isDragging} />
              {canRemove ? (
                <div className={classes.RemoveVariationButtonWrapper}>
                  <Button
                    type={'a'}
                    variant={'clear'}
                    size={'large'}
                    color={ButtonColorsEnum.primary}
                    icon={{ side: 'center', value: IconsEnum.trash }}
                    action={() => {
                      dispatch({
                        type: 'textRemove',
                        payload: { customizeIndex, textId: id, postGroupIndex }
                      });
                    }}
                  />
                </div>
              ) : null}
              <SlateToolbar
                canDelete={canRemove}
                variationId={id}
                addVariation={addVariation}
                editor={editor}
                openAiAssistant={openAiAssistant}
                publishNext={isDragging ? false : publishNext}
                comboErrorMessages={hasError && isLast && !isDragging ? errorMessages : []}
                comboWarningMessages={hasWarning && isLast && !isDragging ? errorMessages : []}
                errorVariationMessages={hasErrorVariation && !isDragging ? errorVariationMessages : []}
                warningVariationMessages={hasWarningVariation && !isDragging ? warningVariationMessages : []}
                {...(addComment ? { addComment } : {})}
                {...(typeof hashtagsCount === 'number' ? { hashtagsCount } : {})}
                emojiTooltip={'Add emojis to your Post'}
                snippetsTooltip={'Add Snippets to your Post, with any text that you re-use frequently.'}
                aiAssistantTooltip={'Rewrite with AI Assistant'}
              />
            </div>
          </div>
          {isSelfDragging ? (
            <div className={classNames(classes.DroppableTarget)} style={{}}>
              <span className={'show-for-sr'}>Target</span>{' '}
            </div>
          ) : null}
        </div>
      </div>
    </CSSTransition>
  );
});

SlateWrapperView.displayName = 'SlateWrapper';
